/**
 * ContainmentBehavior — Enforces BPMN 2.0 parent-child containment rules on top of LogicFlow.
 *
 * Responsibilities:
 *  1. Parent-child registry: tracks which nodes are inside which containers.
 *  2. Group move: moving a container (Pool, Lane, SubProcess, Transaction, Group) moves its children in real-time.
 *  3. Drop rules: when a node is dropped, auto-assign it to the innermost valid container.
 *  4. Resize guard: containers cannot shrink smaller than the bounding box of their children.
 *  5. Connection rules: prevent invalid connections in real-time (SequenceFlow across pools, etc.).
 */

/* ── Type classifications ─────────────────────────────────────────── */

const CONTAINER_TYPES = new Set([
  'epx:Participant', 'epx:Lane', 'epx:SubProcess', 'epx:Transaction', 'epx:Group',
]);

const FLOW_NODE_TYPES = new Set([
  'epx:StartEvent', 'epx:EndEvent',
  'epx:IntermediateEvent', 'epx:IntermediateThrowEvent', 'epx:BoundaryEvent',
  'epx:UserTask', 'epx:ServiceTask', 'epx:ScriptTask', 'epx:ManualTask',
  'epx:SendTask', 'epx:ReceiveTask', 'epx:BusinessRuleTask',
  'epx:CallActivity', 'epx:SubProcess', 'epx:Transaction',
  'epx:ExclusiveGateway', 'epx:ParallelGateway', 'epx:InclusiveGateway',
  'epx:ComplexGateway', 'epx:EventBasedGateway',
]);

const ACTIVITY_TYPES = new Set([
  'epx:UserTask', 'epx:ServiceTask', 'epx:ScriptTask', 'epx:ManualTask',
  'epx:SendTask', 'epx:ReceiveTask', 'epx:BusinessRuleTask',
  'epx:CallActivity', 'epx:SubProcess', 'epx:Transaction',
]);

const DATA_TYPES = new Set(['epx:DataObjectReference', 'epx:DataStoreReference']);
const ARTIFACT_TYPES = new Set(['epx:Group', 'epx:TextAnnotation']);

const POOL_CHILD_TYPES = new Set([...FLOW_NODE_TYPES, ...DATA_TYPES, ...ARTIFACT_TYPES, 'epx:Lane']);
const SUBPROCESS_CHILD_TYPES = new Set([...FLOW_NODE_TYPES, ...DATA_TYPES, ...ARTIFACT_TYPES]);

/* ── Geometry helpers ─────────────────────────────────────────────── */

function getBounds(node) {
  const w = node.width || (node.properties && node.properties.width) || 120;
  const h = node.height || (node.properties && node.properties.height) || 60;
  return {
    left: node.x - w / 2,
    right: node.x + w / 2,
    top: node.y - h / 2,
    bottom: node.y + h / 2,
  };
}

function isInsideBounds(point, bounds) {
  return point.x >= bounds.left && point.x <= bounds.right &&
         point.y >= bounds.top && point.y <= bounds.bottom;
}

/* ── Containment priority (higher = more inner, wins assignment) ─── */

const CONTAINER_DEPTH = {
  'epx:Group': 0,
  'epx:Participant': 1,
  'epx:Lane': 2,
  'epx:SubProcess': 3,
  'epx:Transaction': 3,
};

/* ── Main class ───────────────────────────────────────────────────── */

export class ContainmentBehavior {
  /**
   * @param {import('@logicflow/core').default} lf  LogicFlow instance
   */
  constructor(lf) {
    this._lf = lf;

    // parentId → Set<childId>
    this._children = new Map();
    // childId → parentId
    this._parent = new Map();

    // Drag tracking for real-time child movement
    this._draggingId = null;
    this._lastDragPos = null;
    this._movingChildren = false;

    // BoundaryEvent → hostId registry (separate from parent-child for containment)
    this._boundaryHost = new Map(); // boundaryEventId → hostActivityId

    // Reentrancy guard: prevents resize event loops when relaying out lanes
    this._relayoutingLanes = false;

    this._bindEvents();
  }

  /* ── Public API ──────────────────────────────────────────────────── */

  /** Rebuild the containment registry from scratch (call after importXML). */
  rebuild() {
    this._children.clear();
    this._parent.clear();

    const { nodes } = this._lf.getGraphData();
    if (!nodes || !nodes.length) return;

    // Sort containers by depth so we assign children to the innermost container
    const containers = nodes
      .filter(n => CONTAINER_TYPES.has(n.type))
      .sort((a, b) => (CONTAINER_DEPTH[b.type] || 0) - (CONTAINER_DEPTH[a.type] || 0));

    for (const container of containers) {
      this._children.set(container.id, new Set());
    }

    // For each non-container node, find its innermost container
    const nonContainers = nodes.filter(n => !CONTAINER_TYPES.has(n.type));
    for (const node of nonContainers) {
      this._assignToInnermostContainer(node, containers);
    }

    // Also assign containers to their parent containers (Lane inside Pool, etc.)
    for (const container of containers) {
      const parentCandidates = containers.filter(c =>
        c.id !== container.id && this._canContain(c.type, container.type)
      );
      this._assignToInnermostContainer(container, parentCandidates);
    }

    // Rebuild boundary event → host relationships
    this._boundaryHost.clear();
    for (const node of nodes) {
      if (node.type === 'epx:BoundaryEvent') {
        const hostId = node.properties && node.properties.attachedToRef;
        if (hostId) {
          this._boundaryHost.set(node.id, hostId);
        }
      }
    }

    // Ensure all containers render behind flow nodes
    // Reverse so the deepest containers (SubProcess) end up above pools in z-order
    const sortedContainers = [...containers].sort(
      (a, b) => (CONTAINER_DEPTH[b.type] || 0) - (CONTAINER_DEPTH[a.type] || 0)
    );
    for (const container of sortedContainers) {
      this._sendToBack(container.id);
    }
  }

  getParent(nodeId) {
    return this._parent.get(nodeId) || null;
  }

  getChildren(containerId) {
    return this._children.has(containerId) ? [...this._children.get(containerId)] : [];
  }

  getDescendants(containerId) {
    const result = [];
    const children = this._children.get(containerId);
    if (!children) return result;
    for (const childId of children) {
      result.push(childId);
      result.push(...this.getDescendants(childId));
    }
    return result;
  }

  /** Snap all lanes to their parent pool geometry (call after importXML + rebuild). */
  snapAllLanesToPools() {
    const { nodes } = this._lf.getGraphData();
    if (!nodes) return;
    for (const node of nodes) {
      if (node.type === 'epx:Participant') {
        this._relayoutLanesInPool(node.id);
      }
    }
  }

  /* ── Event binding ───────────────────────────────────────────────── */

  _bindEvents() {
    const lf = this._lf;

    // ── Drag start: record initial position for container drags ──
    lf.on('node:dragstart', ({ data }) => {
      if (this._movingChildren) return;
      if (!CONTAINER_TYPES.has(data.type)) return;
      if (!this._children.has(data.id) || this._children.get(data.id).size === 0) return;

      const model = lf.getNodeModelById(data.id);
      if (!model) return;
      this._draggingId = data.id;
      this._lastDragPos = { x: model.x, y: model.y };
    });

    // ── During drag: move children in real-time using direct model manipulation ──
    lf.on('node:drag', ({ data }) => {
      if (this._movingChildren) return;
      if (this._draggingId !== data.id) return;
      if (!this._lastDragPos) return;

      const model = lf.getNodeModelById(data.id);
      if (!model) return;

      const dx = model.x - this._lastDragPos.x;
      const dy = model.y - this._lastDragPos.y;
      if (dx === 0 && dy === 0) return;

      this._lastDragPos = { x: model.x, y: model.y };

      // Move all descendants by directly setting model properties.
      // This avoids graphModel.moveNodes() which has a bug with stale nodesMap entries.
      this._movingChildren = true;
      try {
        const descendants = this.getDescendants(data.id);
        for (const childId of descendants) {
          const child = lf.getNodeModelById(childId);
          if (!child) continue;
          // Direct property assignment — triggers MobX reactivity for rendering
          // but does NOT trigger LogicFlow's moveNodes/onDragging pipeline
          child.x = child.x + dx;
          child.y = child.y + dy;
          // Also move the text label if it exists
          if (child.text && typeof child.text === 'object' && child.text.value) {
            child.text = {
              ...child.text,
              x: (child.text.x || child.x) + dx,
              y: (child.text.y || child.y) + dy,
            };
          }
        }
        // Update edges connected to moved children
        this._updateEdgesForNodes(descendants);
      } finally {
        this._movingChildren = false;
      }
    });

    // ── Drag end: finalize and reassign parent ──
    lf.on('node:drop', ({ data }) => {
      if (this._movingChildren) return;
      if (this._draggingId === data.id) {
        this._draggingId = null;
        this._lastDragPos = null;
      }
      this._onNodeDropped(data);
    });

    // ── Boundary event drag: snap to host border ──
    lf.on('node:drag', ({ data }) => {
      if (this._movingChildren) return;
      if (data.type !== 'epx:BoundaryEvent') return;
      const hostId = this._boundaryHost.get(data.id);
      if (!hostId) return;
      this._snapBoundaryToHost(data.id, hostId);
    });

    // ── Activity drag: move attached boundary events ──
    lf.on('node:drag', ({ data }) => {
      if (this._movingChildren) return;
      if (!ACTIVITY_TYPES.has(data.type)) return;
      // Find all boundary events attached to this activity
      for (const [beId, hostId] of this._boundaryHost) {
        if (hostId !== data.id) continue;
        const beModel = lf.getNodeModelById(beId);
        const hostModel = lf.getNodeModelById(data.id);
        if (!beModel || !hostModel) continue;
        // Keep boundary event snapped to host border
        this._snapBoundaryToHost(beId, hostId);
      }
    });

    // ── New node from palette: register in containment ──
    lf.on('node:add', ({ data }) => {
      setTimeout(() => {
        // Register boundary event host relationship
        if (data.type === 'epx:BoundaryEvent' && data.properties?.attachedToRef) {
          this._boundaryHost.set(data.id, data.properties.attachedToRef);
        }

        this._onNodeDropped(data);
        // Push container nodes to the back of the render stack so flow nodes
        // added inside them always appear on top (SVG renders last = on top).
        if (CONTAINER_TYPES.has(data.type)) {
          this._sendToBack(data.id);
        }
      }, 0);
    });

    // ── Resize guard + lane sync ──
    lf.on('node:resize', ({ oldNodeSize, newNodeSize }) => {
      if (this._relayoutingLanes) return;
      this._onContainerResize(oldNodeSize, newNodeSize);
      const model = lf.getNodeModelById(newNodeSize.id);
      if (!model) return;
      if (model.type === 'epx:Participant') {
        // Pool resized — relayout all its lanes
        setTimeout(() => this._relayoutLanesInPool(newNodeSize.id), 0);
      } else if (model.type === 'epx:Lane') {
        // Lane resized — do balanced resize with adjacent lane
        setTimeout(() => this._onLaneResize(newNodeSize, oldNodeSize), 0);
      }
    });

    // ── Connection rules ──
    lf.on('edge:add', ({ data }) => {
      this._validateConnection(data);
    });

    // ── Node delete: unregister from containment ──
    lf.on('node:delete', ({ data }) => {
      this._unregister(data.id);
    });
  }

  /* ── Internal: edge updates after child movement ─────────────────── */

  _updateEdgesForNodes(nodeIds) {
    const nodeIdSet = new Set(nodeIds);
    const lf = this._lf;
    const gm = lf.graphModel;
    if (!gm || !gm.edges) return;

    for (const edgeModel of gm.edges) {
      const srcMoved = nodeIdSet.has(edgeModel.sourceNodeId);
      const tgtMoved = nodeIdSet.has(edgeModel.targetNodeId);
      if (!srcMoved && !tgtMoved) continue;

      // Let LogicFlow recalculate edge path based on new anchor positions
      try {
        if (srcMoved) {
          const srcModel = lf.getNodeModelById(edgeModel.sourceNodeId);
          if (srcModel) {
            const anchor = (srcModel.anchors || []).find(a => a.id === edgeModel.sourceAnchorId);
            if (anchor) {
              edgeModel.updateStartPoint({ x: anchor.x, y: anchor.y });
            }
          }
        }
        if (tgtMoved) {
          const tgtModel = lf.getNodeModelById(edgeModel.targetNodeId);
          if (tgtModel) {
            const anchor = (tgtModel.anchors || []).find(a => a.id === edgeModel.targetAnchorId);
            if (anchor) {
              edgeModel.updateEndPoint({ x: anchor.x, y: anchor.y });
            }
          }
        }
      } catch (_) {
        // Edge update may fail if anchors changed — safe to ignore
      }
    }
  }

  /* ── Internal: parent assignment ─────────────────────────────────── */

  _assignToInnermostContainer(node, containers) {
    const point = { x: node.x, y: node.y };
    let bestContainer = null;
    let bestDepth = -1;

    for (const container of containers) {
      if (container.id === node.id) continue;
      if (!this._canContain(container.type, node.type)) continue;

      const cBounds = getBounds(container);
      if (isInsideBounds(point, cBounds)) {
        const depth = CONTAINER_DEPTH[container.type] || 0;
        if (depth > bestDepth) {
          bestDepth = depth;
          bestContainer = container;
        }
      }
    }

    if (bestContainer) {
      this._setParent(node.id, bestContainer.id);
    }
  }

  _setParent(childId, parentId) {
    const oldParent = this._parent.get(childId);
    if (oldParent && this._children.has(oldParent)) {
      this._children.get(oldParent).delete(childId);
    }
    this._parent.set(childId, parentId);
    if (!this._children.has(parentId)) {
      this._children.set(parentId, new Set());
    }
    this._children.get(parentId).add(childId);
  }

  _unregister(nodeId) {
    const parentId = this._parent.get(nodeId);
    if (parentId && this._children.has(parentId)) {
      this._children.get(parentId).delete(nodeId);
    }
    this._parent.delete(nodeId);

    const children = this._children.get(nodeId);
    if (children) {
      for (const childId of children) {
        this._parent.delete(childId);
      }
      this._children.delete(nodeId);
    }

    // Clean up boundary event references
    this._boundaryHost.delete(nodeId);
    // If an activity is deleted, remove boundary events attached to it
    for (const [beId, hostId] of this._boundaryHost) {
      if (hostId === nodeId) {
        this._boundaryHost.delete(beId);
      }
    }
  }

  /* ── Internal: containment rules ─────────────────────────────────── */

  _canContain(containerType, childType) {
    if (containerType === 'epx:Group') return childType !== 'epx:Participant';
    if (containerType === 'epx:Participant') return POOL_CHILD_TYPES.has(childType);
    if (containerType === 'epx:Lane') return SUBPROCESS_CHILD_TYPES.has(childType);
    if (containerType === 'epx:SubProcess' || containerType === 'epx:Transaction') {
      return SUBPROCESS_CHILD_TYPES.has(childType);
    }
    return false;
  }

  /* ── Internal: drop handling ─────────────────────────────────────── */

  _onNodeDropped(data) {
    const { nodes } = this._lf.getGraphData();
    if (!nodes) return;

    if (CONTAINER_TYPES.has(data.type) && !this._children.has(data.id)) {
      this._children.set(data.id, new Set());
    }

    const containers = nodes
      .filter(n => CONTAINER_TYPES.has(n.type) && n.id !== data.id)
      .sort((a, b) => (CONTAINER_DEPTH[b.type] || 0) - (CONTAINER_DEPTH[a.type] || 0));

    const point = { x: data.x, y: data.y };
    let newParent = null;

    for (const container of containers) {
      if (!this._canContain(container.type, data.type)) continue;
      const cBounds = getBounds(container);
      if (isInsideBounds(point, cBounds)) {
        newParent = container.id;
        break;
      }
    }

    const oldParent = this._parent.get(data.id);

    if (newParent) {
      if (oldParent !== newParent) {
        this._setParent(data.id, newParent);
        this._flashContainer(newParent);
      }

      // Auto-relayout lanes when a Lane is placed inside a Participant
      if (data.type === 'epx:Lane') {
        const parentModel = this._lf.getNodeModelById(newParent);
        if (parentModel && parentModel.type === 'epx:Participant') {
          setTimeout(() => {
            this._relayoutLanesInPool(newParent);
            this._redistributeFlowNodesToLanes(newParent);
          }, 0);
        }
      }
    } else if (oldParent) {
      if (this._children.has(oldParent)) {
        this._children.get(oldParent).delete(data.id);
      }
      this._parent.delete(data.id);
    }
  }

  /* ── Internal: resize guard ──────────────────────────────────────── */

  _onContainerResize(oldNodeSize, newNodeSize) {
    const { id } = newNodeSize;
    const children = this._children.get(id);
    if (!children || children.size === 0) return;

    const PADDING = 20;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const childId of children) {
      const childModel = this._lf.getNodeModelById(childId);
      if (!childModel) continue;
      const cb = getBounds(childModel);
      if (cb.left < minX) minX = cb.left;
      if (cb.top < minY) minY = cb.top;
      if (cb.right > maxX) maxX = cb.right;
      if (cb.bottom > maxY) maxY = cb.bottom;
    }

    if (minX === Infinity) return;

    const requiredWidth = (maxX - minX) + PADDING * 2;
    const requiredHeight = (maxY - minY) + PADDING * 2;

    const model = this._lf.getNodeModelById(id);
    if (!model) return;

    let correctedWidth = newNodeSize.width;
    let correctedHeight = newNodeSize.height;
    let needsCorrection = false;

    if (newNodeSize.width < requiredWidth) {
      correctedWidth = requiredWidth;
      needsCorrection = true;
    }
    if (newNodeSize.height < requiredHeight) {
      correctedHeight = requiredHeight;
      needsCorrection = true;
    }

    // Pool minimum height: must fit all lanes at their minimum height
    if (model.type === 'epx:Participant') {
      const laneIds = this.getLanesForPool(id);
      if (laneIds.length > 0) {
        const minPoolHeight = laneIds.length * ContainmentBehavior.MIN_LANE_HEIGHT;
        if (correctedHeight < minPoolHeight) {
          correctedHeight = minPoolHeight;
          needsCorrection = true;
        }
      }
    }

    if (needsCorrection) {
      model.width = correctedWidth;
      model.height = correctedHeight;
      model.setProperties({ width: correctedWidth, height: correctedHeight });
    }
  }

  /* ── Internal: connection rules ──────────────────────────────────── */

  _validateConnection(edgeData) {
    if (!edgeData) return;

    const { nodes } = this._lf.getGraphData();
    if (!nodes) return;
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    const sourceNode = nodeMap.get(edgeData.sourceNodeId);
    const targetNode = nodeMap.get(edgeData.targetNodeId);
    if (!sourceNode || !targetNode) return;

    if (edgeData.type === 'epx:SequenceFlow') {
      const sourcePool = this._findAncestorOfType(edgeData.sourceNodeId, 'epx:Participant');
      const targetPool = this._findAncestorOfType(edgeData.targetNodeId, 'epx:Participant');

      if (sourcePool && targetPool && sourcePool !== targetPool) {
        this._deleteEdgeSafe(edgeData.id);
        this._showWarning('Sequence flows cannot cross pool boundaries. Use Message Flow instead.');
        return;
      }

      if (sourceNode.type === 'epx:Participant' || sourceNode.type === 'epx:Lane' ||
          targetNode.type === 'epx:Participant' || targetNode.type === 'epx:Lane') {
        this._deleteEdgeSafe(edgeData.id);
        this._showWarning('Sequence flows cannot connect to Pools or Lanes directly.');
        return;
      }
    }

    if (edgeData.type === 'epx:MessageFlow') {
      const sourcePool = this._findAncestorOfType(edgeData.sourceNodeId, 'epx:Participant');
      const targetPool = this._findAncestorOfType(edgeData.targetNodeId, 'epx:Participant');

      if (sourcePool && targetPool && sourcePool === targetPool) {
        this._deleteEdgeSafe(edgeData.id);
        this._showWarning('Message flows must connect elements in different pools.');
        return;
      }
    }
  }

  _deleteEdgeSafe(edgeId) {
    try { this._lf.deleteEdge(edgeId); } catch (_) {}
  }

  _findAncestorOfType(nodeId, ancestorType) {
    let current = nodeId;
    const visited = new Set();
    while (current) {
      if (visited.has(current)) break;
      visited.add(current);
      const model = this._lf.getNodeModelById(current);
      if (model && model.type === ancestorType) return current;
      current = this._parent.get(current) || null;
    }
    return null;
  }

  /* ── Z-order management ──────────────────────────────────────────── */

  /**
   * Move a container node to the beginning of graphModel.nodes so it renders
   * behind all flow nodes (SVG paints in array order: first = bottom).
   */
  _sendToBack(nodeId) {
    const gm = this._lf.graphModel;
    if (!gm || !gm.nodes) return;
    const idx = gm.nodes.findIndex(n => n.id === nodeId);
    if (idx <= 0) return; // already at back or not found
    const [node] = gm.nodes.splice(idx, 1);
    gm.nodes.unshift(node);
  }

  /* ── Visual feedback ─────────────────────────────────────────────── */

  _flashContainer(containerId) {
    const container = this._lf.container;
    if (!container) return;
    const el = container.querySelector(`[data-element-id="${containerId}"]`)
      || container.querySelector(`g[id$="${containerId}"]`);
    if (!el) return;
    el.classList.add('epx-containment-highlight');
    setTimeout(() => el.classList.remove('epx-containment-highlight'), 600);
  }

  _showWarning(message) {
    const container = this._lf.container;
    if (!container) return;
    const toast = document.createElement('div');
    toast.className = 'epx-containment-toast';
    toast.textContent = message;
    container.appendChild(toast);
    setTimeout(() => {
      toast.classList.add('epx-containment-toast-hide');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  /* ── Boundary event snap ────────────────────────────────────────── */

  /**
   * Snap a boundary event to the nearest point on its host activity's border.
   */
  _snapBoundaryToHost(boundaryId, hostId) {
    const lf = this._lf;
    const beModel = lf.getNodeModelById(boundaryId);
    const hostModel = lf.getNodeModelById(hostId);
    if (!beModel || !hostModel) return;

    const hb = getBounds(hostModel);
    const px = beModel.x;
    const py = beModel.y;

    // Find the nearest point on the host rectangle's perimeter
    const clampedX = Math.max(hb.left, Math.min(px, hb.right));
    const clampedY = Math.max(hb.top, Math.min(py, hb.bottom));

    // Distances to each edge from the clamped point
    const dLeft = Math.abs(clampedX - hb.left);
    const dRight = Math.abs(clampedX - hb.right);
    const dTop = Math.abs(clampedY - hb.top);
    const dBottom = Math.abs(clampedY - hb.bottom);

    const minDist = Math.min(dLeft, dRight, dTop, dBottom);

    let snapX = clampedX;
    let snapY = clampedY;

    if (minDist === dBottom) {
      snapY = hb.bottom;
      snapX = clampedX;
    } else if (minDist === dTop) {
      snapY = hb.top;
      snapX = clampedX;
    } else if (minDist === dRight) {
      snapX = hb.right;
      snapY = clampedY;
    } else {
      snapX = hb.left;
      snapY = clampedY;
    }

    // Clamp along the edge to keep within bounds
    if (snapY === hb.top || snapY === hb.bottom) {
      snapX = Math.max(hb.left, Math.min(snapX, hb.right));
    }
    if (snapX === hb.left || snapX === hb.right) {
      snapY = Math.max(hb.top, Math.min(snapY, hb.bottom));
    }

    beModel.x = snapX;
    beModel.y = snapY;
  }

  /* ── Lane subdivision API ────────────────────────────────────────── */

  /** Pool header width — lanes start after this offset */
  static POOL_HEADER_WIDTH = 30;
  /** Minimum lane height */
  static MIN_LANE_HEIGHT = 60;

  /**
   * Get all lane IDs that belong to a given Participant, sorted top to bottom.
   */
  getLanesForPool(poolId) {
    const children = this._children.get(poolId);
    if (!children) return [];
    const lf = this._lf;
    const lanes = [];
    for (const childId of children) {
      const model = lf.getNodeModelById(childId);
      if (model && model.type === 'epx:Lane') {
        lanes.push({ id: childId, y: model.y, height: model.height });
      }
    }
    lanes.sort((a, b) => a.y - b.y);
    return lanes.map(l => l.id);
  }

  /**
   * Add the first lane to a pool — subdivides the pool into one lane that
   * fills the entire body. User can then add more lanes above/below.
   */
  addLaneToPool(poolId) {
    const lf = this._lf;
    const poolModel = lf.getNodeModelById(poolId);
    if (!poolModel) return null;

    const existingLanes = this.getLanesForPool(poolId);
    if (existingLanes.length > 0) {
      // Pool already has lanes — add a new lane by splitting the last one
      return this.addLaneBelow(existingLanes[existingLanes.length - 1]);
    }

    const pw = poolModel.width;
    const ph = poolModel.height;
    const hdr = ContainmentBehavior.POOL_HEADER_WIDTH;

    // Lane fills the pool body (right of the pool header)
    const laneWidth = pw - hdr;
    const laneHeight = ph;
    const laneX = poolModel.x + hdr / 2;
    const laneY = poolModel.y;

    const node = lf.addNode({
      type: 'epx:Lane',
      x: laneX,
      y: laneY,
      properties: {
        name: '',
        width: laneWidth,
        height: laneHeight,
      },
    });

    if (node) {
      this._setParent(node.id, poolId);
      this._sendToBack(node.id);
      this._sendToBack(poolId);
    }
    return node;
  }

  /**
   * Add a new lane below the given lane (splits the space).
   */
  addLaneBelow(laneId) {
    return this._insertLane(laneId, 'below');
  }

  /**
   * Add a new lane above the given lane (splits the space).
   */
  addLaneAbove(laneId) {
    return this._insertLane(laneId, 'above');
  }

  _insertLane(refLaneId, position) {
    const lf = this._lf;
    const refModel = lf.getNodeModelById(refLaneId);
    if (!refModel) return null;

    const poolId = this._parent.get(refLaneId);
    if (!poolId) return null;

    const poolModel = lf.getNodeModelById(poolId);
    if (!poolModel) return null;

    const lanes = this.getLanesForPool(poolId);
    const refIdx = lanes.indexOf(refLaneId);
    if (refIdx === -1) return null;

    // Calculate new lane height: split the reference lane's height in half
    const refHeight = refModel.height;
    const newHeight = Math.max(refHeight / 2, ContainmentBehavior.MIN_LANE_HEIGHT);
    const shrunkHeight = Math.max(refHeight - newHeight, ContainmentBehavior.MIN_LANE_HEIGHT);
    const actualNewHeight = refHeight - shrunkHeight;

    if (position === 'below') {
      // Shrink reference lane, place new lane below it
      const refTop = refModel.y - refHeight / 2;
      refModel.height = shrunkHeight;
      refModel.y = refTop + shrunkHeight / 2;
      refModel.setProperties({ height: shrunkHeight });

      const newY = refTop + shrunkHeight + actualNewHeight / 2;
      const node = lf.addNode({
        type: 'epx:Lane',
        x: refModel.x,
        y: newY,
        properties: {
          name: '',
          width: refModel.width,
          height: actualNewHeight,
        },
      });

      if (node) {
        this._setParent(node.id, poolId);
        // Shift all lanes below down if needed
        this._relayoutLanesInPool(poolId);
        this._sendToBack(node.id);
        this._sendToBack(poolId);
      }
      return node;
    } else {
      // Above: shrink reference lane, place new lane above it
      const refTop = refModel.y - refHeight / 2;
      refModel.height = shrunkHeight;
      refModel.y = refTop + actualNewHeight + shrunkHeight / 2;
      refModel.setProperties({ height: shrunkHeight });

      const newY = refTop + actualNewHeight / 2;
      const node = lf.addNode({
        type: 'epx:Lane',
        x: refModel.x,
        y: newY,
        properties: {
          name: '',
          width: refModel.width,
          height: actualNewHeight,
        },
      });

      if (node) {
        this._setParent(node.id, poolId);
        this._relayoutLanesInPool(poolId);
        this._sendToBack(node.id);
        this._sendToBack(poolId);
      }
      return node;
    }
  }

  /**
   * Remove a lane and redistribute its space to adjacent lanes.
   */
  removeLane(laneId) {
    const lf = this._lf;
    const poolId = this._parent.get(laneId);
    if (!poolId) return;

    const lanes = this.getLanesForPool(poolId);
    if (lanes.length <= 1) {
      // Can't remove the last lane — delete it and the pool has no lanes
      this._unregister(laneId);
      lf.deleteNode(laneId);
      return;
    }

    const laneModel = lf.getNodeModelById(laneId);
    if (!laneModel) return;

    const idx = lanes.indexOf(laneId);
    const removedHeight = laneModel.height;

    // Give the removed lane's space to the adjacent lane
    const adjacentId = idx > 0 ? lanes[idx - 1] : lanes[idx + 1];
    const adjacentModel = lf.getNodeModelById(adjacentId);
    if (adjacentModel) {
      adjacentModel.height = adjacentModel.height + removedHeight;
      adjacentModel.setProperties({ height: adjacentModel.height });
    }

    this._unregister(laneId);
    lf.deleteNode(laneId);
    this._relayoutLanesInPool(poolId);
  }

  /**
   * Relayout all lanes in a pool — stack them vertically, ensure they fill the pool body.
   *
   * Invariants enforced:
   *  - Lane width  = pool.width - HEADER_WIDTH
   *  - Lane center X = pool.x + HEADER_WIDTH / 2
   *  - Lanes stack top-to-bottom with zero gaps
   *  - Last lane absorbs rounding error so combined heights == pool height exactly
   */
  _relayoutLanesInPool(poolId) {
    if (this._relayoutingLanes) return;
    this._relayoutingLanes = true;
    try {
      this._doRelayoutLanes(poolId);
    } finally {
      this._relayoutingLanes = false;
    }
  }

  _doRelayoutLanes(poolId) {
    const lf = this._lf;
    const poolModel = lf.getNodeModelById(poolId);
    if (!poolModel) return;

    const laneIds = this.getLanesForPool(poolId);
    if (laneIds.length === 0) return;

    const hdr = ContainmentBehavior.POOL_HEADER_WIDTH;
    const poolLeft = poolModel.x - poolModel.width / 2;
    const poolTop = poolModel.y - poolModel.height / 2;
    const poolHeight = poolModel.height;
    const laneWidth = poolModel.width - hdr;
    const laneCenterX = poolLeft + hdr + laneWidth / 2;

    // Collect lane models and their current heights
    const laneModels = [];
    let totalLaneHeight = 0;
    for (const id of laneIds) {
      const m = lf.getNodeModelById(id);
      if (m) {
        laneModels.push(m);
        totalLaneHeight += m.height;
      }
    }
    if (laneModels.length === 0) return;

    // If pool needs to grow to fit minimum lane heights
    const minRequired = laneModels.length * ContainmentBehavior.MIN_LANE_HEIGHT;
    if (poolHeight < minRequired) {
      poolModel.height = minRequired;
      poolModel.y = poolTop + minRequired / 2;
      poolModel.setProperties({ height: minRequired });
      // Recalculate with new height
      this._doRelayoutLanes(poolId);
      return;
    }

    // Distribute heights proportionally; last lane absorbs rounding error
    let currentY = poolTop;
    let usedHeight = 0;

    for (let i = 0; i < laneModels.length; i++) {
      const m = laneModels[i];
      let h;

      if (i === laneModels.length - 1) {
        // Last lane takes all remaining space — no rounding gap
        h = poolHeight - usedHeight;
      } else {
        h = Math.round((m.height / totalLaneHeight) * poolHeight);
        h = Math.max(h, ContainmentBehavior.MIN_LANE_HEIGHT);
      }

      m.x = laneCenterX;
      m.y = currentY + h / 2;
      m.width = laneWidth;
      m.height = h;
      m.setProperties({ width: laneWidth, height: h });

      currentY += h;
      usedHeight += h;
    }
  }

  /**
   * Handle lane resize — balanced resize with adjacent lane.
   * Forces lane width/x back to pool-synced values and adjusts the
   * adjacent lane to absorb the vertical height delta.
   */
  _onLaneResize(newNodeSize, oldNodeSize) {
    if (this._relayoutingLanes) return;

    const lf = this._lf;
    const laneId = newNodeSize.id;
    const poolId = this._parent.get(laneId);
    if (!poolId) return;

    const poolModel = lf.getNodeModelById(poolId);
    const laneModel = lf.getNodeModelById(laneId);
    if (!poolModel || !laneModel) return;

    const hdr = ContainmentBehavior.POOL_HEADER_WIDTH;
    const laneWidth = poolModel.width - hdr;
    const laneCenterX = poolModel.x + hdr / 2;

    // Force horizontal sync — prevent independent horizontal resize
    laneModel.width = laneWidth;
    laneModel.x = laneCenterX;

    // Balanced vertical resize: adjacent lane absorbs the height delta
    const heightDelta = newNodeSize.height - oldNodeSize.height;
    if (heightDelta !== 0) {
      const lanes = this.getLanesForPool(poolId);
      const idx = lanes.indexOf(laneId);

      // Detect resize direction by checking if center moved up (top drag) or down (bottom drag)
      const adjacentIdx = (newNodeSize.y < oldNodeSize.y)
        ? idx - 1   // top edge moved up → shrink lane above
        : idx + 1;  // bottom edge moved down → shrink lane below

      if (adjacentIdx >= 0 && adjacentIdx < lanes.length) {
        const adjacentModel = lf.getNodeModelById(lanes[adjacentIdx]);
        if (adjacentModel) {
          let newAdjacentHeight = adjacentModel.height - heightDelta;
          if (newAdjacentHeight < ContainmentBehavior.MIN_LANE_HEIGHT) {
            // Clamp adjacent, reduce this lane's resize accordingly
            const excess = ContainmentBehavior.MIN_LANE_HEIGHT - newAdjacentHeight;
            newAdjacentHeight = ContainmentBehavior.MIN_LANE_HEIGHT;
            laneModel.height = newNodeSize.height - excess;
          }
          adjacentModel.height = newAdjacentHeight;
          adjacentModel.setProperties({ height: newAdjacentHeight });
        }
      } else {
        // No adjacent lane — grow/shrink the pool instead
        const poolTop = poolModel.y - poolModel.height / 2;
        poolModel.height = poolModel.height + heightDelta;
        poolModel.y = poolTop + poolModel.height / 2;
        poolModel.setProperties({ height: poolModel.height });
      }
    }

    // Final relayout to ensure pixel-perfect stacking
    this._relayoutLanesInPool(poolId);
  }

  /**
   * Redistribute flow nodes inside a pool to the correct lane based on position.
   */
  _redistributeFlowNodesToLanes(poolId) {
    const lf = this._lf;
    const laneIds = this.getLanesForPool(poolId);
    if (laneIds.length === 0) return;

    // Get all non-lane children of the pool
    const children = this._children.get(poolId);
    if (!children) return;

    const flowNodeIds = [...children].filter(id => {
      const m = lf.getNodeModelById(id);
      return m && m.type !== 'epx:Lane';
    });

    // Build lane bounds
    const laneBounds = laneIds.map(id => {
      const m = lf.getNodeModelById(id);
      return m ? { id, bounds: getBounds(m) } : null;
    }).filter(Boolean);

    for (const nodeId of flowNodeIds) {
      const nodeModel = lf.getNodeModelById(nodeId);
      if (!nodeModel) continue;

      // Find the lane whose bounds contain this node
      let bestLane = null;
      for (const lb of laneBounds) {
        if (isInsideBounds({ x: nodeModel.x, y: nodeModel.y }, lb.bounds)) {
          bestLane = lb.id;
          break;
        }
      }

      // If found a lane, reassign to lane instead of pool
      if (bestLane) {
        this._setParent(nodeId, bestLane);
      }
    }
  }

  static injectStyles() {
    if (document.getElementById('epx-containment-styles')) return;
    const style = document.createElement('style');
    style.id = 'epx-containment-styles';
    style.textContent = `
      @keyframes epx-containment-pulse {
        0% { filter: drop-shadow(0 0 0 rgba(24,144,255,0)); }
        50% { filter: drop-shadow(0 0 8px rgba(24,144,255,0.5)); }
        100% { filter: drop-shadow(0 0 0 rgba(24,144,255,0)); }
      }
      .epx-containment-highlight {
        animation: epx-containment-pulse 0.6s ease-out;
      }
      .epx-containment-toast {
        position: absolute;
        bottom: 16px;
        left: 50%;
        transform: translateX(-50%);
        background: #ff4d4f;
        color: #fff;
        padding: 8px 16px;
        border-radius: 4px;
        font-size: 13px;
        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
        z-index: 10000;
        box-shadow: 0 2px 8px rgba(0,0,0,.2);
        transition: opacity 0.3s;
        pointer-events: none;
      }
      .epx-containment-toast-hide {
        opacity: 0;
      }
    `;
    document.head.appendChild(style);
  }
}
